// src/components/IsEmirleri.js

import React, { useState, useEffect, useMemo } from 'react';
import { normalizeTr, aramaMetniUret } from '../utils/search';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, runTransaction, getDocs, where, deleteField } from 'firebase/firestore';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, IconButton, Typography, Paper, CircularProgress, TextField, InputAdornment, Chip, Button, Grid, Stack, Divider, Tooltip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CurrencyLiraIcon from '@mui/icons-material/CurrencyLira';
import { normalizeRandevuDurum, VARSAYILAN_RANDEVU_DURUMU, KAPANMIS_RANDEVU_DURUMLARI } from './AppointmentConstants';

// Kapanmış randevu (Tamamlandı/İptal) iş emri silinince YENİDEN AÇILMAZ —
// açılsaydı teslim edilmiş bir araç takvimde tekrar "bekleyen randevu" olarak belirirdi.
// Dizi tek kaynaktan (AppointmentConstants) gelir; aynı kural sunucu tarafında da
// var: functions/cascade.js KAPANMIS_DURUMLAR.

// Durum Renkleri
const getStatusChipColor = (status) => {
  switch (status) {
    case 'Beklemede': return 'warning';
    case 'İşlemde': return 'info';
    case 'Tamamlandı': return 'success';
    case 'Teslime Hazır': return 'secondary';
    case 'Teslim Edildi': return 'default';
    default: return 'default';
  }
};

// Logo Getirici
const getLogoPath = (markaModel) => {
  if (!markaModel || typeof markaModel !== 'string') return '/logos/default.svg';
  const marka = markaModel.split(' ')[0].toLowerCase().replace(' ', '-');
  return `/logos/${marka}.svg`;
};

const IsEmirleri = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [durumFiltre, setDurumFiltre] = useState('Tümü');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, "workOrders"), orderBy("isEmriTarihi", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setWorkOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleDelete = async (e, id, plaka) => {
    e.stopPropagation();
    const onayMetni =
      `'${plaka}' plakalı araca ait iş emri silinecek.\n\n` +
      `• Müşteri carisinden bu iş emrinin tutarı geri alınacak.\n` +
      `• İş emrine girilmiş ödeme kayıtları (peşinat dahil) da silinecek.\n` +
      `• Bağlı randevu varsa iş emri bağı kopacak; açık randevu "Bekleniyor" durumuna dönecek ` +
      `("Tamamlandı"/"İptal" randevu yeniden açılmaz).\n\n` +
      `BU İŞLEM GERİ ALINAMAZ. Devam edilsin mi?`;
    if (!window.confirm(onayMetni)) return;

    const workOrderRef = doc(db, 'workOrders', id);
    try {
      // Transaction sorgu çalıştıramaz: silinecek alt-doküman ve bağlı randevu referansları önce toplanır.
      const paymentsSnap = await getDocs(collection(workOrderRef, 'payments'));
      const paymentRefs = paymentsSnap.docs.map(d => d.ref);
      const appointmentsSnap = await getDocs(
        query(collection(db, 'appointments'), where('workOrderId', '==', id))
      );
      // Randevunun mevcut durumu burada okunur: kapanmış randevuya YALNIZCA bağ koparma yazılır.
      const appointmentRefs = appointmentsSnap.docs.map(d => ({
        ref: d.ref,
        kapanmis: KAPANMIS_RANDEVU_DURUMLARI.includes(normalizeRandevuDurum(d.data().durum))
      }));

      await runTransaction(db, async (transaction) => {
        const workOrderDoc = await transaction.get(workOrderRef);
        if (!workOrderDoc.exists()) throw new Error("İş emri bulunamadı!");

        const workOrderData = workOrderDoc.data();
        const customerRef = workOrderData.musteriId ? doc(db, "customers", workOrderData.musteriId) : null;
        const customerDoc = customerRef ? await transaction.get(customerRef) : null;

        if (customerDoc?.exists()) {
          const customerData = customerDoc.data();
          const toplam = workOrderData.toplamTutar || 0;
          const odenen = workOrderData.odenenTutar || 0;
          // kalanTutar alanına güvenilmez (bozuk olabilir): toplam - ödenen yeniden hesaplanır.
          const kalan = toplam - odenen;

          transaction.update(customerRef, {
            toplamSatis: (customerData.toplamSatis || 0) - toplam,
            toplamOdeme: (customerData.toplamOdeme || 0) - odenen,
            toplamBakiye: (customerData.toplamBakiye || 0) - kalan
          });
        }

        // Firestore alt-koleksiyonları cascade silmez -> yetim ödeme kaydı kalmasın
        paymentRefs.forEach(ref => transaction.delete(ref));

        // Bağlı randevu serbest kalsın (yetim workOrderId + kilitli randevu detayı biter).
        // 'Tamamlandı'/'İptal' randevunun DURUMUNA dokunulmaz — yalnız bağ kopar.
        appointmentRefs.forEach(({ ref, kapanmis }) => transaction.update(
          ref,
          kapanmis
            ? { workOrderId: deleteField() }
            : { workOrderId: deleteField(), durum: VARSAYILAN_RANDEVU_DURUMU }
        ));

        transaction.delete(workOrderRef);
      });
      alert("İş emri, ödeme kayıtları ve randevu bağı temizlendi; müşteri bakiyesi geri alındı.");
    } catch (err) {
      console.error("Hata:", err);
      alert(`Hata: ${err?.message || err}`);
    }
  };

  // G8: Türkçe-normalize çok alanlı arama (plaka + ad + telefon), boşluk katlamalı.
  const filteredOrders = useMemo(() => {
    const q = normalizeTr(searchTerm).replace(/\s/g, '');
    return workOrders.filter(order => {
      const matchSearch = !q ||
        aramaMetniUret(order.aracPlaka, order.musteriAdSoyad, order.musteriTelefon).replace(/\s/g, '').includes(q);
      const matchDurum = durumFiltre === 'Tümü' || order.durum === durumFiltre;
      return matchSearch && matchDurum;
    });
  }, [workOrders, searchTerm, durumFiltre]);

  if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />;

  return (
    <Box>
      {/* Üst Başlık ve Arama */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ width: '100%', fontWeight: 'bold', color: 'primary.main' }}>İş Emirleri</Typography>
        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Plaka veya Müşteri Ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
            sx={{ width: '100%', bgcolor: 'background.paper' }}
          />
          <Button variant="contained" onClick={() => navigate('/is-emri-olustur')} startIcon={<AddCircleOutlineIcon />} sx={{ flexShrink: 0, whiteSpace: 'nowrap', py: 1 }}>
            Yeni İş Emri
          </Button>
        </Box>
      </Box>

      {/* Durum Filtresi */}
      <Box sx={{ mb: 2, overflowX: 'auto' }}>
        <ToggleButtonGroup
          value={durumFiltre}
          exclusive
          onChange={(e, val) => { if (val !== null) setDurumFiltre(val); }}
          size="small"
          sx={{ mb: 1 }}
        >
          <ToggleButton value="Tümü">Tümü ({workOrders.length})</ToggleButton>
          <ToggleButton value="İşlemde">İşlemde</ToggleButton>
          <ToggleButton value="Tamamlandı">Tamamlandı</ToggleButton>
          <ToggleButton value="Teslime Hazır">Teslime Hazır</ToggleButton>
          <ToggleButton value="Teslim Edildi">Teslim Edildi</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Kart Listesi (Grid) */}
      <Grid container spacing={2}>
        {filteredOrders.map((order) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 6, cursor: 'pointer' },
                borderLeft: `4px solid`,
                borderColor: order.kalanTutar > 0 ? 'error.main' : 'success.main'
              }}
              onClick={() => navigate(`/is-emri/${order.id}`)}
            >
              {/* Başlık: Logo, Plaka ve Durum */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 50, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', borderRadius: 1, p: 0.5, border: '1px solid #eee' }}>
                    <Box component="img" src={getLogoPath(order.aracMarkaModel)} onError={(e) => { e.currentTarget.src = '/logos/default.svg'; }} alt={order.aracPlaka} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{order.aracPlaka}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{order.aracMarkaModel}</Typography>
                  </Box>
                </Box>
                <Chip label={order.durum} color={getStatusChipColor(order.durum)} size="small" />
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              {/* Detaylar */}
              <Stack spacing={1} sx={{ mb: 2, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2" noWrap>{order.musteriAdSoyad}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon fontSize="small" color="action" />
                  <Typography variant="body2">{order.isEmriTarihi?.toDate().toLocaleDateString('tr-TR')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CurrencyLiraIcon fontSize="small" color={order.kalanTutar > 0 ? "error" : "success"} />
                  <Typography variant="body2" fontWeight="bold" color={order.kalanTutar > 0 ? "error.main" : "success.main"}>
                    {order.kalanTutar > 0 ? `Kalan: ${order.kalanTutar.toLocaleString('tr-TR')} ₺` : "Ödeme Tamamlandı"}
                  </Typography>
                </Box>
              </Stack>

              {/* Butonlar */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 'auto', pt: 1, borderTop: '1px solid #eee' }}>
                <Tooltip title="Detay"><IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/is-emri/${order.id}`); }}><VisibilityIcon /></IconButton></Tooltip>
                {currentUser && (currentUser.role === 'Genel Müdür' || currentUser.role === 'Müdür') && (
                  <>
                    <Tooltip title="Düzenle"><IconButton size="small" color="info" onClick={(e) => { e.stopPropagation(); navigate(`/is-emri/duzenle/${order.id}`); }}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Sil"><IconButton size="small" color="error" onClick={(e) => handleDelete(e, order.id, order.aracPlaka)}><DeleteIcon /></IconButton></Tooltip>
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}

        {filteredOrders.length === 0 && (
          <Grid item xs={12}>
            <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>Kayıtlı iş emri bulunamadı.</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
export default IsEmirleri;